import { Component, Input, EventEmitter, Output, OnInit, OnDestroy } from '@angular/core';

import { BbTableService, TableRow } from '../../../services/bb-table.service';
import { OverlayService } from '../../../services/overlay.service';
import { SapiEntity } from '../../../services/sapi.service';
import { PublicationService } from 'app/services/publication.service';
import { FilterSetService } from 'app/services/filter-set.service';

import { Project } from 'app/interfaces/project';
import { Mediaclip } from '../../../interfaces/mediaclip';
import { UntypedFormGroup } from '@angular/forms';
import { AbstractListComponent, ListConfiguration } from '../abstract-list.component';
import { first } from 'rxjs/operators';
import { BbSelectOption } from '../bb-select/bb-select.component';
import { QuickeditService } from 'app/services/quickedit.service';
import { ClipboardService } from 'ngx-clipboard';

export interface React extends SapiEntity {
	oid: number;
	otype: string;
	playoutname: string;
	hash: string;
	label?: string;
	context?: string;
	notifications: boolean;
	password?: string;
	password_set?: boolean;
	keep_password?: boolean;
	timelineversion?: 'published' | 'draft';
}

/**
 * This component allows to create a React page for the entity
 *
 * - entity: mediaclip or project
 * - is saved as a seperate entity in the database (table: react)
 */

@Component({
	selector: 'bb-react',
	templateUrl: './bb-react.component.html',
	styleUrls: ['./bb-react.component.scss'],
	providers: [BbTableService, FilterSetService]
})
export class BbReactComponent extends AbstractListComponent implements OnInit, OnDestroy {
	@Input() entity: Mediaclip | Project;
	@Output() onChange = new EventEmitter();

	reactItems: React[];
	reactForm = new UntypedFormGroup({});

	listConfiguration: ListConfiguration;

	public entityType: string;
	public projectContextOptions: BbSelectOption[];

	constructor(
		protected bbTableService: BbTableService,
		protected overlayService: OverlayService,
		private publicationService: PublicationService,
		private quickeditService: QuickeditService,
		private clipboard: ClipboardService
	) {
		super(bbTableService);
	}

	ngOnInit() {
		this.entityType = this.entity.type.toLowerCase();
		if (this.entityType === 'project' && (this.entity as Project).contexts?.length > 0) {
			this.projectContextOptions = (this.entity as Project).contexts.map(c => (
				{ label: c.label, value: c.code }
			));
		}
		this.initAbstractListConfig();

		this.bbTableService.setLimit(10);
		// lookup React item by mediaclipId or projectId
		this.bbTableService.setFilterParam(this.entityType + 'Id', this.entity.id);

		super.ngOnInit();
	}

	// override super redirectToDetails (doubleclick / mobile)
	public redirectToDetails(row: TableRow) {
		if (row?.data?.hash && !row.quickEditing) {
			this.openReactPage(row.data.hash);
		}
	}

	// cancel triggered by parent component / action bar
	public cancel(): void {
		// collapses all in quickEdit and resets data
		this.quickeditService.removeSaveActions();
		this.bbTableService.getRows(true);
	}

	trackBy(index: number, item: any) {
		if (item.id !== 'new') {
			return item.id || index;
		}
		return index;
	}

	// deleted in quick edit
	onItemRemoved(row: TableRow, i: number): void {
		super.deleteSingle(row.data);
	}

	openReactPage(hash: string): void {
		const reactUrl = this.getUrl(hash);
		window.open(reactUrl);
	}

	copyUrlToClipboard(hash: string): void {
		const reactUrl = this.getUrl(hash);
		this.clipboard.copyFromContent(reactUrl);
		this.overlayService.createToast(this.translate.instant('URL_COPIED_TO_CLIPBOARD'), 'copy');
	}

	createReactPage(): void {
		const reactItem: Partial<React> = {
			oid: +this.entity.id,
			otype: this.entityType,
			playoutname: 'default',
			label: this.translate.instant('NEW_REACT_PAGE'),
			notifications: true,
			password: '',
			timelineversion: 'published'
		};

		this.overlayService.setLoading(true);
		this.sapiService.create('react', reactItem).subscribe((react: React) => {
			this.rows.unshift({
				data: react
			});
			const reactTotal = this.rows.map(row => row.data);

			if (this.bbTableService.getCurrentPage() !== 1) {
				this.bbTableService.setData(reactTotal).setPage(1);
				this.bbTableService.rowsRetrievedObservable.pipe(first()).subscribe(() => {
					this.openQuickEditAfterTableInsert(react.id);
				});
			} else {
				this.bbTableService.setData(reactTotal);
				this.overlayService.setLoading(false);
				this.bbTableService.totalChangedObservable.pipe(first()).subscribe(() => {
					this.openQuickEditAfterTableInsert(react.id);
				});
			}
		}, () => {
			this.overlayService.setLoading(false);
		});
	}

	private getUrl(hash: string): string {
		return `https://${this.publicationService.baseUrl}/react/#/?hash=${hash}`;
	}

	private openQuickEditAfterTableInsert(id: string): void {
		const index = this.rows.findIndex(row => row.data.id === id);
		if (index > -1) {
			this.rows[index].quickEditing = true;
		}
	}

	private initAbstractListConfig(): void {
		this.listConfiguration = {
			name: 'react',
			basePath: `/library/${this.entityType}/${this.entity.id}/react`,
			tableConfigurable: false
		};
		this.filterConfig = {
			search: false
		};
	}
}
